import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { CheckboxAlignButtonEnum } from '@inter-ikea-kompis/component-checkbox';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';

import RadioLayoutEnum from '../../enums/RadioLayoutEnum.js';
import RadioButtonStyle from './RadioButtonStyle.js';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 * This component should be used for radio buttons.
 *
 * @event select Emits when a radio button is selected
 */

export default class RadioButton extends KompisElement {
	// "true" if checked.
	@property({ type: Boolean })
	public checked = false;

	// "true" if disabled.
	@property({ type: Boolean })
	public disabled = false;

	// Layout.
	@property({ type: String })
	public layout: RadioLayoutEnum = RadioLayoutEnum.emphasised;

	// Index for radio input
	@property({ type: String })
	public name = '';

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	@property({ type: String })
	public label: string | null = null;

	// Where the label should be.
	@property({ type: String })
	public alignButton: CheckboxAlignButtonEnum = CheckboxAlignButtonEnum.left;

	@property({ type: Boolean })
	public hovered = false;

	private hasFocus = false;
	private pointerIsDown = false;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const direction = window.getComputedStyle(this).direction;

		return html`
			<style>
				${RadioButtonStyle.getStyle({
					isDisabled: this.disabled,
					isChecked: this.checked,
					layout: this.layout,
					hasFocus: this.hasFocus,
					direction: direction,
					theme: this.theme,
					alignButton: this.alignButton,
					hover: this.hovered
				})}
			</style>
			${this.getRadioButton()}
		`;
	}
	public override connectedCallback(): void {
		this.customElements.define('kompis-icon', Icon);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	/**
	 * When focusing.
	 */
	public override focus(): void {
		this.shadowRoot?.querySelector('input')?.focus();
	}

	private getRadioButton(): RenderResult {
		// The keyup is necessary to enable Enter input on Chromium browsers.
		return html`
			<div
				class="container"
				@click="${this.onClick.bind(this)}"
				@keyup="${(event: KeyboardEvent) => event.key === 'Enter' && this.click()}"
			>
				<input
					type="radio"
					@touchdown="${this.onPointerDown.bind(this)}"
					@pointerdown="${this.onPointerDown.bind(this)}"
					@focusout="${this.onFocusOut.bind(this)}"
					@focus="${this.onFocus.bind(this)}"
					?disabled="${this.disabled}"
					.checked="${this.checked}"
					name="${this.name}"
				/>
				<div class="radioButtonContainer">
					<div class="outline"></div>
					<div class="radioSymbol"></div>
				</div>
				${this.getLabel()}
			</div>
		`;
	}

	private getLabel(): RenderResult {
		if (!this.label) {
			return null;
		}

		return html`
			<div class="divider"></div>
			<kompis-text class="label" .theme="${this.theme}"> ${this.label} </kompis-text>
		`;
	}

	/**
	 * Called when radio button checked value has changed.
	 */
	private onClick(): void {
		if (!this.disabled) {
			this.dispatchEvent(
				new CustomEvent('select', {
					bubbles: true,
					detail: {
						checked: !this.checked
					}
				})
			);
		}
	}

	/**
	 * HTML event handler.
	 */
	private onFocusOut(): void {
		this.hasFocus = false;
		this.pointerIsDown = false;
		this.update();
	}

	/**
	 * HTML event handler.
	 */
	private onFocus(): void {
		if (this.pointerIsDown) {
			return;
		}
		this.hasFocus = true;
		this.update();
	}

	/**
	 * HTML event handler.
	 */
	private onPointerDown(): void {
		this.pointerIsDown = true;
		if (this.hasFocus) {
			this.hasFocus = false;
			this.update();
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-radio-button': RadioButton;
	}
}
